import { useQuery, UseQueryResult } from "@tanstack/react-query";
import getConfig from "@/utils/getConfig";

interface LootboxTransactionHistoryItem {
    id: string;
    amount: number;
    amountFormatted: string;
    status: string;
    description: string;
    cartItems: any[];
    createdAt: string;
}

interface LootboxTransactionHistoryResponse {
    transactions: LootboxTransactionHistoryItem[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
    timeFilter: string;
    startDate: string;
    endDate: string;
}

interface UseGetTransactionHistoryLootboxesParams {
    timeFilter?: "day" | "week" | "month" | "year" | "all";
    enabled?: boolean;
}

export const useGetTransactionHistoryLootboxes = (
    fullToken: string | undefined,
    params: UseGetTransactionHistoryLootboxesParams = {},
): UseQueryResult<LootboxTransactionHistoryResponse, Error> => {
    const { timeFilter = "all", enabled = true } = params;

    return useQuery({
        queryKey: ["transactionHistoryLootboxes-v1", fullToken, timeFilter],
        queryFn: async (): Promise<LootboxTransactionHistoryResponse> => {
            if (!fullToken) {
                throw new Error("Full token is required for lootbox transaction history");
            }

            const config = getConfig({ token: fullToken });
            const url = new URL("/devourplay/transaction-history-lootboxes", config.basePath);

            // Add time filter to query params
            if (timeFilter !== "all") {
                url.searchParams.append("timeFilter", timeFilter);
            }

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${fullToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json();
        },
        enabled: enabled && !!fullToken,
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: false,
    });
};